import { configureAuth } from '@artic/api-client';
import { clearAuthCookies } from '@artic/ui/lib/auth-cookies';
import { clearAppLocalStorage } from '@artic/ui/lib/supabase-cookie-storage';
import { FeaturesOptions, FeaturesProvider } from '@saas-ui-pro/feature-flags';
import { ModalsProvider } from '@saas-ui/modals';
import { SaasProvider } from '@saas-ui/react';
import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import { supabaseIntegration } from '@supabase/sentry-js-integration';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { IntercomProvider } from 'react-use-intercom';
import { RecoilRoot } from 'recoil';

import packageJson from '../package.json';
import App from './App';

import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lightgallery.css';
import { theme } from './theme';
import i18n from './utils/i18n';
import { shouldIgnoreError } from './utils/sentryIgnoreRules';
import { supabase } from './utils/supabaseClient';

console.info(`[artic-hub] v${packageJson.version} (${__COMMIT_SHA__}) built ${__BUILD_TIME__}`);

Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_SENTRY_ENVIRONMENT,
  release: packageJson.version,
  // BrowserTracing settings - does performance monitoring
  tracesSampleRate: 0.01,
  // Replay settings - records and replays sessions
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.browserProfilingIntegration(),
    supabaseIntegration(supabase, Sentry, {
      tracing: true,
      breadcrumbs: true,
      errors: true,
    }),
  ],
  beforeSend(event: ErrorEvent, hint: EventHint) {
    const error = hint?.originalException;
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    // @supabase/sentry-js-integration rewraps DOMException as a plain Error,
    // preserving the "AbortError: signal is aborted..." message.
    if (error instanceof Error && /signal is aborted/i.test(error.message)) {
      return null;
    }
    // Drop third-party injected-code noise (browser extensions / embedded
    // browsers) that pollutes Sentry as if it were our bugs. See
    // ./utils/sentryIgnoreRules for the per-signature rationale.
    const candidateMessage =
      error instanceof Error
        ? error.message
        : (event.exception?.values?.[0]?.value ?? event.message ?? '');
    if (shouldIgnoreError(candidateMessage)) {
      return null;
    }
    return event;
  },
});

const options: FeaturesOptions = {
  segments: [
    {
      id: 'admin',
      attr: [
        {
          key: 'admin',
          value: true,
        },
      ],
      features: ['profiles'],
    },
  ],
};

const queryClient = new QueryClient();

const cookieDomain = import.meta.env.VITE_AUTH_COOKIE_DOMAIN || undefined;

configureAuth({
  cookieDomain,
  onRefreshFailure: () => {
    // api-client has already cleared the artic cookies and latched off
    // any further refresh attempts. Tear down Supabase + app state here
    // BEFORE we navigate, so the bounce to /login lands on a clean slate:
    // - stopAutoRefresh halts the 30s ticker hitting /auth/v1/token; left
    //   running it keeps amplifying the rate-limit cascade until unload.
    // - signOut({ scope: 'local' }) wipes Supabase storage and broadcasts
    //   SIGNED_OUT to sibling tabs so they don't sit on a stale session.
    // - clearAppLocalStorage drops cached partner/factory/etc.
    // - clearAuthCookies is belt-and-braces with the api-client clear.
    if (window.location.pathname.startsWith('/login')) return;
    void supabase.auth.stopAutoRefresh();
    void supabase.auth.signOut({ scope: 'local' }).catch(() => {
      /* best-effort — storage may already be empty */
    });
    clearAppLocalStorage();
    clearAuthCookies({ domain: cookieDomain });
    window.location.replace('/login');
  },
});

// Unregister any legacy service workers from the removed vite-plugin-pwa setup.
// Without this, old SWs serve stale cached assets indefinitely.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    })
    .catch(() => {
      // Best-effort cleanup; some browsers reject getRegistrations() with UnknownError.
    });
}

// Recover a long-open session after a new release: a lazily-imported route or
// vendor chunk can 404 when its content-hashed file was replaced by a newer
// build. Vite fires `vite:preloadError` for these failed dynamic imports — reload
// to pull the current index.html and its new chunk names. A short-lived
// sessionStorage latch avoids a reload loop if the reload can't fix it (e.g. a
// genuinely stale index.html would keep pointing at the missing chunk).
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_KEY = 'artic-hub:last-chunk-reload';
  const now = Date.now();
  let lastReload = 0;
  try {
    lastReload = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    // sessionStorage may be blocked (private mode); proceed without the loop guard.
  }
  if (now - lastReload < 10_000) return; // reloaded moments ago — let the error surface
  event.preventDefault();
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // best-effort — no loop guard if storage is unavailable
  }
  window.location.reload();
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <FeaturesProvider value={options}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <IntercomProvider appId={import.meta.env.VITE_APP_INTERCOM_APP_ID} autoBoot>
            <RecoilRoot>
              <SaasProvider theme={theme}>
                <ModalsProvider>
                  <App />
                </ModalsProvider>
              </SaasProvider>
            </RecoilRoot>
          </IntercomProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </FeaturesProvider>
  </React.StrictMode>,
);
